import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design06/Header';
import {
    Coffee,
    Globe,
    Award,
    Shield,
    MapPin,
    Mail,
    Phone,
    Users,
    CheckCircle,
    ArrowRight,
    Leaf,
    Heart,
    Zap,
    Target,
    TrendingUp,
    Package,
    ThumbsUp,
} from 'lucide-react';

const HERO_IMG = 'https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg';

const HomePage = () => {
    useEffect(() => {
        // ---- Simple head manager (no Helmet) ----
        const setMeta = (attr, key, content) => {
            let el = document.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement('meta');
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            if (content != null) el.setAttribute('content', content);
        };

        const setLink = (rel, href) => {
            let el = document.querySelector(`link[rel="${rel}"]`);
            if (!el) {
                el = document.createElement('link');
                el.setAttribute('rel', rel);
                document.head.appendChild(el);
            }
            el.setAttribute('href', href);
        };

        document.title =
            "Claude's Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices";

        setMeta(
            'name',
            'description',
            "Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
        );
        setMeta(
            'name',
            'keywords',
            'Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import'
        );

        // Open Graph
        setMeta('property', 'og:title', "Claude's Special - Premium Ceylon Cinnamon Exports");
        setMeta(
            'property',
            'og:description',
            "Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
        );
        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:image', HERO_IMG);
        setMeta('property', 'og:image:alt', 'Ceylon cinnamon bark and traditional processing');

        // Twitter
        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', "Claude's Special - Premium Ceylon Cinnamon Exports");
        setMeta(
            'name',
            'twitter:description',
            'Authentic Ceylon Cinnamon from Sri Lanka to Australia.'
        );
        setMeta('name', 'twitter:image', HERO_IMG);

        // Canonical (use your real domain if you have one)
        setLink('canonical', '/');
    }, []);

    return (
        <>
            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Ceylon Cinnamon"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
                </div>

                <div className="relative z-10 text-center max-w-6xl mx-auto px-8 pt-20">
                    <div className="mb-8">
                        <span className="inline-block px-6 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-200 text-sm font-medium tracking-wider uppercase rounded-full">
                            Authentic Ceylon Cinnamon
                        </span>
                    </div>

                    <h1 className="text-6xl md:text-8xl font-serif font-bold mb-8 leading-tight">
                        <span className="block text-white mb-4">The World's</span>
                        <span className="block bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                            Finest Spice
                        </span>
                    </h1>

                    <p className="text-xl md:text-2xl mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed font-light">
                        From the mystical highlands of Sri Lanka to the discerning markets of Australia,
                        we bring you the authentic taste of true cinnamon.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
                        <Link
                            to="/ceylon-cinnamon"
                            className="group bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Discover Our Story</span>
                            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                        <a
                            href="#about"
                            className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105"
                        >
                            Learn More
                        </a>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 pb-16">
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-400 mb-2">25+</div>
                            <div className="text-gray-300 font-medium">Years Experience</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-400 mb-2">100%</div>
                            <div className="text-gray-300 font-medium">Authentic</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-400 mb-2">50+</div>
                            <div className="text-gray-300 font-medium">Partners</div>
                        </div>
                        <div className="text-center">
                            <div className="text-4xl font-bold text-amber-400 mb-2">99.9%</div>
                            <div className="text-gray-300 font-medium">Quality</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src={HERO_IMG}
                                    alt="Traditional cinnamon processing"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="absolute -bottom-8 -right-8 bg-gradient-to-br from-amber-600 to-orange-700 p-8 rounded-2xl shadow-2xl text-white">
                                <div className="text-center">
                                    <Coffee className="h-12 w-12 mx-auto mb-4" />
                                    <div className="text-2xl font-bold">Premium</div>
                                    <div className="text-lg">Heritage</div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <div className="mb-6">
                                <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium tracking-wider uppercase rounded-full">
                                    Our Heritage
                                </span>
                            </div>

                            <h2 className="text-5xl font-serif font-bold text-gray-900 mb-8 leading-tight">
                                Crafting Excellence
                                <br />
                                <span className="text-amber-600">Since 1999</span>
                            </h2>

                            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                                For over two decades, Claude's Special has been the trusted bridge between
                                Sri Lanka's ancient cinnamon traditions and Australia's most discerning markets.
                            </p>

                            <div className="space-y-6 mb-10">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Certified Excellence</h3>
                                        <p className="text-gray-600">
                                            ISO certification with HACCP compliance ensuring international quality standards.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                                        <Users className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Artisan Partnerships</h3>
                                        <p className="text-gray-600">
                                            Direct relationships with master craftsmen ensuring sustainable practices.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                        <Globe className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Global Excellence</h3>
                                        <p className="text-gray-600">
                                            Seamless export operations connecting Sri Lankan quality to world markets.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/ceylon-cinnamon"
                                className="inline-flex items-center bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white px-8 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg space-x-3"
                            >
                                <span>Explore Our Products</span>
                                <ArrowRight className="h-5 w-5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center mb-16">
                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium tracking-wider uppercase rounded-full">
                                Why Choose Us
                            </span>
                        </div>
                        <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Unmatched Excellence</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Every aspect of our operation is designed to deliver the finest Ceylon Cinnamon
                            with uncompromising quality and service.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Shield className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Assurance</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Rigorous testing and quality control ensures only the finest Ceylon Cinnamon reaches your customers.
                                </p>
                                <div className="flex items-center text-amber-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>ISO Certified</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 2 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Zap className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Fast Export</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Efficient logistics and streamlined processes ensure quick delivery to Australia.
                                </p>
                                <div className="flex items-center text-blue-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>5-10 Days</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 3 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Leaf className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Sustainable</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Ethical partnerships with local farmers ensure environmental sustainability.
                                </p>
                                <div className="flex items-center text-green-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Eco-Friendly</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 4 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Package className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Flexible Packaging</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Custom packaging solutions from bulk wholesale to retail-ready formats.
                                </p>
                                <div className="flex items-center text-purple-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Custom Solutions</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 5 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <ThumbsUp className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Expert Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Dedicated specialists provide personalized support throughout your journey.
                                </p>
                                <div className="flex items-center text-yellow-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>24/7 Support</span>
                                </div>
                            </div>
                        </div>

                        {/* Feature 6 */}
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <TrendingUp className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Competitive Pricing</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Premium Ceylon Cinnamon at competitive wholesale prices with transparent costs.
                                </p>
                                <div className="flex items-center text-teal-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Best Value</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Ceylon Cinnamon"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-8 lg:px-16 relative">
                    <div className="mb-8">
                        <span className="inline-block px-6 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-200 text-sm font-medium tracking-wider uppercase rounded-full">
                            Ready to Start?
                        </span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-serif font-bold text-white mb-8 leading-tight">
                        Begin Your Journey with
                        <br />
                        <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                            Authentic Ceylon Cinnamon
                        </span>
                    </h2>

                    <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto">
                        Join Australia's most successful importers who trust Claude's Special for authentic,
                        premium Ceylon Cinnamon that exceeds expectations.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <a
                            href="#contact"
                            className="group bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Get Quote</span>
                            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </a>
                        <Link
                            to="/ceylon-cinnamon"
                            className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105"
                        >
                            Explore Products
                        </Link>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center mb-16">
                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium tracking-wider uppercase rounded-full">
                                Get In Touch
                            </span>
                        </div>
                        <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">Ready to Import?</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Contact our export specialists to discuss your Ceylon Cinnamon requirements
                            and discover how we can serve your business.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
                                <h3 className="text-3xl font-semibold text-gray-900 mb-8">Export Office</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                                            <MapPin className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1 text-lg">Location</h4>
                                            <p className="text-gray-600">Colombo, Sri Lanka</p>
                                            <p className="text-amber-600 font-medium">Export Processing Zone</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                                            <Phone className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1 text-lg">Phone</h4>
                                            <p className="text-gray-600">+94 11 XXX XXXX</p>
                                            <p className="text-blue-600 font-medium">Export Hotline</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                                            <Mail className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1 text-lg">Email</h4>
                                            <p className="text-gray-600">exports@claudesspecial.lk</p>
                                            <p className="text-green-600 font-medium">24/7 Response</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 shadow-lg">
                            <h3 className="text-3xl font-semibold text-gray-900 mb-8">Export Inquiry</h3>
                            <form
                                className="space-y-6"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    // TODO: handle form submit (send to API / email service)
                                }}
                            >
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                                            Company Name *
                                        </label>
                                        <input
                                            id="company"
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                                            placeholder="Your company"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                                            Contact Person *
                                        </label>
                                        <input
                                            id="contactPerson"
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                                            placeholder="Your name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email Address *
                                    </label>
                                    <input
                                        id="email"
                                        type="email"
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                                        placeholder="your@company.com"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        id="message"
                                        rows={6}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200 resize-none"
                                        placeholder="Tell us about your import requirements..."
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white py-4 text-lg font-medium rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-3"
                                >
                                    <span>Send Inquiry</span>
                                    <ArrowRight className="h-5 w-5" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-4 mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center">
                                <Coffee className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <div className="text-3xl font-serif font-bold text-white">Claude's Special</div>
                                <div className="text-amber-400 font-medium">Ceylon Cinnamon Exports</div>
                            </div>
                        </div>
                        <div className="w-24 h-0.5 bg-gradient-to-r from-amber-500 to-orange-600 mx-auto mb-8"></div>
                        <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
                            The world's finest Ceylon Cinnamon, sourced directly from Sri Lanka's pristine highlands
                            and delivered to Australia with uncompromising quality and authenticity.
                        </p>
                        <div className="flex justify-center space-x-12 text-gray-300 mb-8">
                            <span className="flex items-center space-x-2">
                                <Coffee className="h-5 w-5 text-amber-400" />
                                <span className="font-medium">Premium Quality</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Shield className="h-5 w-5 text-green-400" />
                                <span className="font-medium">Certified</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Globe className="h-5 w-5 text-blue-400" />
                                <span className="font-medium">Global Export</span>
                            </span>
                        </div>
                        <p className="text-gray-500 font-medium">&copy; 2025 Claude&apos;s Special. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default HomePage;
